import "./insights_setup.scss";
import { useContext, useEffect, useState } from "react";
import AppLifecycleContext from "@/lib/contexts/AppLifecycleContext";
import { ConditionItem } from "types/types.d";
import { InsightsService } from "@/lib/services/InsightsService";
import { NameSetup } from "../Components/Setup/NameSetup";
import { EventsSetup } from "../Components/Setup/EventsSetup";
import { useNavigate } from "react-router-dom";
import { LoadingComponent } from "@/ui/components/Common/Loading/Loading";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "@/lib/utils/Constants";
import { InsightContext } from "@/lib/contexts/InsightContext";
import useInsightHook from "@/hooks/useInsightHook";
import { ThirdPartyTracking } from "@/lib/utils/ThirdPartyTracking";
import { Button } from "@mantine/core";
import { SidePanel } from "@/ui/components/Common/SidePanel/SidePanel";
import useConditionsValidityHook from "@/hooks/useConditionsValidityHook";
import useInsightsListHook from "@/hooks/useInsightsListHook";

interface ISlippingAwayUsersSetupModal {
  isModalOpen: boolean;
  setIsModalOpen: (e: boolean) => void;
  insightId?: string;
}

const SlippingAwayUsersSetupModal = ({
  isModalOpen,
  setIsModalOpen,
  insightId
}: ISlippingAwayUsersSetupModal) => {
  // States that are used.
  const [isLoading, setIsLoading] = useState("idle");
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [conditions, setConditions] = useState<Array<ConditionItem>>([]);

  const [isStoreLoader, setIsStoreLoader] = useState("idle");
  const navigate = useNavigate();
  const { stepsValidation } = useConditionsValidityHook();
  // We are passing attributes and events to the child components.
  const { insightEvents, insightAttributes } = useInsightHook();
  const { activeWorkspace } = useContext(AppLifecycleContext);
  const { insightsList } = useInsightsListHook("retention");

  // NOTE: We are going to provide cohort filter in insights as discussed internally.

  // const [audienceConditionType, setAudienceConditionType] = useState<
  //   "or" | "and"
  // >("or");
  // const [audienceConditions, setAudienceConditions] = useState<
  //   Array<ConditionItem>
  // >([]);

  // all service calls
  const insightService = new InsightsService();
  const createInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .create(activeWorkspace.id, {
        name: name,
        conditions: {
          slipped_away_activity_conditions: conditions
        },
        type: "slipping_away_users"
      })
      .then((res) => {
        if (res.data) {
          toast.success(TOAST_MESSAGES.INSIGHT_CREATED);
          const TPService = new ThirdPartyTracking();
          TPService.track("slipping_away_users_insight_created");
          setIsModalOpen(false);
          // as user is already on /env/${activeWorkspace.identifier}/insights/dashboard/retention
          navigate({ search: `insightId=${res.data.id}` });
        }
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const updateInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .update(activeWorkspace.id, templateId, {
        name: name,
        conditions: {
          slipped_away_activity_conditions: conditions
        }
      })
      .then((res) => {
        toast.success(TOAST_MESSAGES.INSIGHT_UPDATED);
        setIsModalOpen(false);
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const getInsight = async () => {
    setIsLoading("loading");
    await insightService
      .get(activeWorkspace.id, insightId)
      .then((res) => {
        if (res.data) {
          setTemplateId(res.data.id);
          setName(res.data.name);
          setConditions(res.data.conditions.slipped_away_activity_conditions);
        }
      })
      .catch((err) => {});
    setIsLoading("loaded");
  };

  useEffect(() => {
    if (insightId) {
      getInsight();
    } else {
      setIsLoading("loaded");
    }
  }, [insightId]);

  if (isLoading !== "loaded") {
    return (
      <>
        <LoadingComponent></LoadingComponent>
      </>
    );
  }

  const handleSubmission = async () => {
    if (name.length === 0) {
      toast.error("Please enter a name for your insight.");
      return;
    }

    // Check if the insight name already exists
    if (
      insightsList &&
      insightsList.find(
        (insight: any) =>
          insight.name.toLowerCase() === name.trim().toLowerCase() &&
          insight.id !== insightId &&
          insight.type === "slipping_away_users"
      )
    ) {
      toast.error("An insight with the same name already exists. Please enter a different name.");
      return;
    }

    const slippingAwayUsersConditionsCheck = stepsValidation(conditions, 1, 10);
    if (!slippingAwayUsersConditionsCheck) {
      return;
    }

    if (insightId) {
      await updateInsight();
    } else {
      await createInsight();
    }
  };

  return (
    <SidePanel
      opened={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      loading={true}
      title={"Slipping Away Users"}>
      <NameSetup
        name={name}
        setName={setName}
        placeholder={"e.g Users who have published content..."}
      />
      <InsightContext.Provider
        value={{
          insightEvents,
          insightAttributes
        }}>
        <EventsSetup
          conditions={conditions}
          setConditions={setConditions}
          title={"Select events that show some product usage"}
          description={"Select the events you use to measure engagement in your product."}
          conditionType={"or"}
        />
      </InsightContext.Provider>
      <div className="um-insights-setup__actions">
        <Button
          onClick={() => {
            setIsModalOpen(false);
          }}
          color="red">
          Cancel
        </Button>
        {templateId ? (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Update Insight
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Save Insight
            </Button>
          </>
        )}
      </div>
    </SidePanel>
  );
};

export default SlippingAwayUsersSetupModal;
